<script setup lang="ts">
import type { FieldSize } from '@/components/fields/KTextField.vue';

import { computed, ref } from 'vue';

import KCheckboxField from '@/components/fields/KCheckboxField.vue';
import NoDataSelectVListItem from '@/components/items/NoDataSelectVListItem.vue';
import useAutocompleteField from '@/composables/useAutocompleteField';
import useField from '@/composables/useField';

import fieldStyles from '@/styles/field.module.scss';
import listItemStyles from '@/styles/listItem.module.scss';

export interface ItemProps {
  subtitle?: string;
  titleIcon?: string;
}

export interface Item {
  value: string | number;
  title: string;
  props?: ItemProps;
}

export interface Props {
  modelValue?: Item[] | string[] | number[] | null;
  label?: string;
  size?: FieldSize;
  items?: readonly Item[] | readonly string[] | readonly number[];
  readonly?: boolean;
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  label: undefined,
  size: 'md',
  items: () => [],
  readonly: false,
  disabled: false,
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref(props.modelValue);
const search = ref('');
const field = ref<HTMLFormElement | null>(null);

useField(props, fieldValue, (newFieldValue) => {
  emit('update:modelValue', newFieldValue as (typeof props)['modelValue']);
});
const { handleEnterKeydown } = useAutocompleteField(fieldValue, search, field);

const sizeClass = computed(() => 'size-' + props.size);
</script>

<template>
  <v-autocomplete
    ref="field"
    v-model="fieldValue"
    v-model:search="search"
    :items="props.items"
    :label="props.label"
    :class="[
      'g-v-text-field',
      $style.kMultipleAutocompleteField,
      fieldStyles[sizeClass],
    ]"
    data-testid="kMultipleAutocompleteField"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    :clearable="!props.readonly"
    :menu-icon="props.readonly ? '' : 'mdi-chevron-down'"
    :readonly="props.readonly"
    :disabled="props.disabled"
    auto-select-first
    multiple
    chips
    :closable-chips="!props.readonly"
    :filter-keys="['title', 'props.subtitle']"
    transition="slide-y-transition"
    @keydown.enter="handleEnterKeydown"
  >
    <template #no-data>
      <NoDataSelectVListItem />
    </template>
    <template #chip="itemData">
      <v-chip
        :class="$style.chip"
        variant="flat"
        :color="$style.chipColor"
        v-bind="itemData.props"
        :prepend-icon="itemData.item.props.titleIcon"
        label
      >
      </v-chip>
    </template>
    <template #item="itemData">
      <v-list-item
        :class="listItemStyles.listItem"
        :active-class="listItemStyles.listItemActive"
        v-bind="itemData.props"
      >
        <template #prepend="{ isSelected }">
          <v-list-item-action>
            <KCheckboxField
              class="my-n2"
              tabindex="-1"
              :model-value="isSelected"
              hide-details
            />
          </v-list-item-action>
        </template>
        <template #title="{ title }">
          <v-icon
            v-if="itemData.item.props.titleIcon"
            :class="listItemStyles.titleIcon"
            :icon="itemData.item.props.titleIcon"
          >
          </v-icon>
          {{ title }}
        </template>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
  chipColor: colors.$color-brand-200;
}

.kMultipleAutocompleteField {
  :global {
    .v-field__input input {
      margin: 0;
      padding: 0;
    }
    .v-autocomplete__selection {
      margin-top: 0;
      margin-inline-end: 6px !important;
    }
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}

.chip {
  color: colors.$color-text-primary !important;

  :global {
    .v-chip__prepend {
      font-size: 10px;
    }
    .v-chip__content {
      font-size: 16px;
    }
    .v-chip__close {
      background: transparent;
      border: 0;
      margin: 0;
      padding: 0;
      font-size: 16px;
    }
  }
}
</style>
